import type { MockOrder, OrderStatus } from '../../../mocks/trackingOrders';
import { allSteps, statusConfig } from '../../../mocks/trackingOrders';

interface OrderTimelineProps {
  order: MockOrder;
}

const stepOrder: OrderStatus[] = ['confirmed', 'preparing', 'shipped', 'transit', 'delivered'];

const OrderTimeline = ({ order }: OrderTimelineProps) => {
  const currentIndex = stepOrder.indexOf(order.status);
  const cfg = statusConfig[order.status];

  return (
    <div className="bg-white border border-stone-100 rounded-2xl p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs text-stone-400 font-body uppercase tracking-widest mb-1">Número de pedido</p>
          <h2 className="font-display text-xl font-semibold text-stone-900">{order.id}</h2>
        </div>
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium font-body ${cfg.bgColor} ${cfg.color}`}>
          <div className={`w-5 h-5 flex items-center justify-center`}>
            <i className={`${cfg.icon} text-base`} />
          </div>
          {cfg.label}
        </div>
      </div>

      {/* Progress Steps */}
      <div className="relative mb-10">
        {/* Progress bar track */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-stone-100" />
        <div
          className="absolute top-5 left-0 h-0.5 bg-amber-600 transition-all duration-700"
          style={{ width: `${(currentIndex / (stepOrder.length - 1)) * 100}%` }}
        />

        <div className="relative flex justify-between">
          {allSteps.map((step, idx) => {
            const done = idx <= currentIndex;
            const active = idx === currentIndex;

            return (
              <div key={step.key} className="flex flex-col items-center gap-2 flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 z-10 ${
                    active
                      ? 'bg-amber-700 text-white ring-4 ring-amber-100'
                      : done
                      ? 'bg-amber-600 text-white'
                      : 'bg-stone-100 text-stone-400'
                  }`}
                >
                  <i className={`${step.icon} text-sm`} />
                </div>
                <span
                  className={`text-xs font-body text-center leading-tight hidden sm:block ${
                    active ? 'text-amber-700 font-semibold' : done ? 'text-stone-600' : 'text-stone-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Delivery info */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { icon: 'ri-calendar-check-line', label: 'Entrega estimada', value: order.estimatedDelivery },
          { icon: 'ri-truck-line', label: 'Transportista', value: order.carrier },
          { icon: 'ri-barcode-line', label: 'Número de seguimiento', value: order.trackingNumber },
        ].map((item) => (
          <div key={item.label} className="bg-stone-50 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-4 h-4 flex items-center justify-center text-amber-700">
                <i className={`${item.icon} text-sm`} />
              </div>
              <p className="text-xs text-stone-400 font-body uppercase tracking-wide">{item.label}</p>
            </div>
            <p className="text-sm font-medium text-stone-800 font-body">{item.value}</p>
          </div>
        ))}
      </div>

      {/* Event log */}
      <div>
        <h3 className="text-xs font-body uppercase tracking-widest text-stone-400 mb-5">Historial de eventos</h3>
        <div className="relative">
          <div className="absolute left-[19px] top-0 bottom-0 w-px bg-stone-100" />
          <div className="flex flex-col gap-5">
            {order.events.map((event, idx) => (
              <div key={idx} className="relative flex items-start gap-4 pl-10">
                <div
                  className={`absolute left-0 w-10 h-10 flex items-center justify-center z-10 ${
                    idx === 0 ? 'text-amber-700' : 'text-stone-400'
                  }`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full ${idx === 0 ? 'bg-amber-600' : 'bg-stone-300'}`} />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-0.5 sm:gap-4 flex-1 pb-1">
                  <div>
                    <p className={`text-sm font-body ${idx === 0 ? 'text-stone-900 font-medium' : 'text-stone-600'}`}>
                      {event.description}
                    </p>
                    {event.location && (
                      <p className="text-xs text-stone-400 font-body mt-0.5 flex items-center gap-1">
                        <i className="ri-map-pin-line text-xs" />
                        {event.location}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-body text-stone-500">{event.date}</p>
                    <p className="text-xs font-body text-stone-400">{event.time}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTimeline;
